import { Component, OnInit, ViewEncapsulation, HostListener } from '@angular/core';
import { ClientHierarchy, TileData } from '../../../shared/interfaces/user-location';
import { HierarchyService } from '../../services/hierarchy.service';
import { UntypedFormGroup } from '@angular/forms';
import { EmployeeService } from '../../../shared/services/employee.service';
import { ToastService } from '../../../shared/services/toast.service';
import { HierarchyEntity } from '../../interfaces/hierarchy-data';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { SortDescriptor, orderBy, filterBy } from '@progress/kendo-data-query';
import { Employee } from '../../../shared/interfaces/client-data';
import { forkJoin } from 'rxjs';
import { ImpersonationService } from '../../../shared/services/impersonation.service';
import { CurrentUserStateService } from '../../../shared/services/current-user-state.service';

interface TabList {
    id: number;
    name: string;
    icon?: string;
    disabled: boolean;
    activeTabIndex: number;
    roleOrder: number;
}

interface TabHiererchy {
    roleOrder: string;
    tileName: string;
    reference: number;
    isActive: boolean;
}

@Component({
    selector: 'hierarchy-config',
    templateUrl: './hierarchy-config.component.html',
    styleUrls: ['./hierarchy-config.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class HierarchyConfigComponent implements OnInit {
    shouldParentBeCorporateEntity: boolean;
    tabBarItems: TabList[];
    corporateHierarchy: TabHiererchy[];
    isMainSaveBtnDisabled: boolean;
    isNextBtnDisabled: boolean;
    isSaveBtnDisabled: boolean;
    isCancelBtnDisabled: boolean;
    selectedTab: number;
    showHierarchySetup: boolean;
    isButtonSetHidden: boolean;
    isLevelSetUpButtonSetHidden: boolean = true;
    entities: HierarchyEntity[];
    unknownEntities: HierarchyEntity[];
    entitySearchTerm: string;
    parentSearchTerm: string;
    selectedEntity: HierarchyEntity;
    filteredEntities: HierarchyEntity[];
    filteredParents: HierarchyEntity[];
    id: string;
    entityGridView: GridDataResult;
    parentGridView: GridDataResult;
    info = true;
    type: 'numeric' | 'input' = 'input';
    pageSizes = true;
    previousNext = true;
    pageSize = 10;
    skip = 0;
    mySelection: number[] = [];
    entitySort: SortDescriptor[] = [{ field: 'levelOrder', dir: 'desc' }];
    stepNumber: number;
    createView: boolean;
    editView: boolean;
    addOrEditEntityForm: UntypedFormGroup;
    possibleOwners: Employee[];
    employeeSearchTerm: string;
    employees: Employee[];
    selectedEmployee: Employee;
    filteredEmployees: Employee[];
    employeeGridView: GridDataResult;
    employeeSort: SortDescriptor[] = [{ field: 'lastName', dir: 'asc' }];
    levels: ClientHierarchy[];
    levelLabel: string;
    showCsvUploadDialog: boolean;
    fileMaxSizeInMB: number;
    fileMaxCount: number;
    uploadType: string;
    corporateEntity: HierarchyEntity;
    onUnknownTab: boolean;
    loading: boolean = false;
    activeCustomRoleNames: string[];

    constructor(
        private _hierarchyService: HierarchyService,
        private employeeService: EmployeeService,
        private currentUserStateService: CurrentUserStateService,
        private impersonationService: ImpersonationService,
        private toastService: ToastService
    ) {}

    ngOnInit() {
        this.selectedTab = 0;
        this.corporateHierarchy = [];
        this.showHierarchySetup = true;
        this.isButtonSetHidden = false;
        this.createView = false;
        this.editView = false;
        this.entitySearchTerm = '';
        this.parentSearchTerm = '';
        let clientShortName = this.currentUserStateService.currentUser.clientShortName;
        if (clientShortName === 'LBA' && !this.impersonationService.isImpersonatingClient()) {
            return;
        }
        if (this.impersonationService.isImpersonatingClient()) {
            clientShortName = this.impersonationService.impersonateClient;
        }
        this.loadLevelsAndEntities();
        this.showCsvUploadDialog = false;
        this.fileMaxSizeInMB = 500;
        this.fileMaxCount = 1;
        this.uploadType = 'CSV';
        this.getEmployeesForClient();
    }

    setHierarchyLevels(hierarchies: TileData[]) {
        if (hierarchies) {
            this.corporateHierarchy = [];
            for (let hierarchy of hierarchies) {
                this.initializecorporateHierarchy(hierarchy.tileData);
            }
            this.reloadLevels();
            this.reloadEntities();
        }
    }

    setCorporateEntity(): void {
        this.corporateEntity = this.entities.find((x) => x.levelOrder === 10);
    }

    setParentToCorporateEntity(): void {
        this.addOrEditEntityForm
            .get('createOrEditEntityRequest')
            .get('parentName')
            .setValue(this.corporateEntity.name);
        this.addOrEditEntityForm
            .get('createOrEditEntityRequest')
            .get('parentKey')
            .setValue(this.corporateEntity.hierarchyKey);
    }

    isNextLevelUpCorporate(): boolean {
        this.shouldParentBeCorporateEntity =
            this.levels.filter((level) => +level.roleOrder > this.getRoleOrder()).length === 1;
        return this.shouldParentBeCorporateEntity;
    }

    getRoleOrder(): number {
        return this.tabBarItems[this.selectedTab].roleOrder;
    }

    setTopTabBarItems() {
        this.tabBarItems = [];
        if (this.levels && this.levels.length) {
            this.levels.forEach((level, index) => {
                let name = level.roleName;

                if (level.isActive && level.roleName.toLowerCase() !== 'employee') {
                    if (level.roleName.toLowerCase() === 'corporate') {
                        name = 'Level Setup';
                    }
                    this.tabBarItems.push({
                        name: name,
                        id: index,
                        roleOrder: +level.roleOrder,
                        disabled: false,
                        activeTabIndex: index,
                    });
                }
            });
        }
    }

    log() {
        console.log(this.addOrEditEntityForm);
    }

    addUnknownTabIfNeeded(): void {
        if (this.unknownEntities.length) {
            this.tabBarItems.push({
                name: 'Unknown',
                id: this.tabBarItems[this.tabBarItems.length - 1].activeTabIndex + 1,
                roleOrder: -1,
                disabled: false,
                activeTabIndex: this.tabBarItems[this.tabBarItems.length - 1].activeTabIndex + 1,
            });
        }
    }

    initializecorporateHierarchy(value: ClientHierarchy) {
        this.corporateHierarchy.push({
            reference: value.reference,
            roleOrder: value.roleOrder.toString(),
            tileName: value.roleName,
            isActive: value.isActive,
        });
    }

    setNextButtonDisableStatus() {
        if (this.selectedTab === this.tabBarItems.length - 1) {
            this.isNextBtnDisabled = true;
        } else {
            this.isNextBtnDisabled = false;
        }
    }

    setSaveButtonDisableStatus() {
        if (this.selectedTab === 0) {
            this.isSaveBtnDisabled = true;
            this.isCancelBtnDisabled = true;
        } else {
            this.isSaveBtnDisabled = false;
            this.isCancelBtnDisabled = false;
        }
    }

    setCancelButtonDisableStatus() {
        if (this.selectedTab === 0) {
            this.isSaveBtnDisabled = true;
        } else {
            this.isSaveBtnDisabled = false;
        }
    }

    sideSheetOpenStatus(sideSheetStatus: boolean) {
        if (sideSheetStatus) {
            this.isButtonSetHidden = true;
        } else {
            this.isButtonSetHidden = false;
        }
    }

    getEmployeesForClient(): void {
        this.employeeService.getAllEmployees().subscribe(
            (employees) => {
                this.employees = employees;
                this.loadEmployeesIntoGridView(true);
            },
            (error) => {
                throw new Error(error);
            }
        );
    }

    employeeSelectionChangeHandler(event: any): void {
        this.addOrEditEntityForm
            .get('createOrEditEntityRequest')
            .get('ownerIdentifier')
            .setValue(event.selectedRows[0].dataItem.primaryIdentifier);
        this.addOrEditEntityForm
            .get('createOrEditEntityRequest')
            .get('owner')
            .setValue(event.selectedRows[0].dataItem.lfName);
        this.employeeSearchTerm = '';
        this.addOrEditEntityForm.markAsDirty();
    }

    loadEmployeesIntoGridView(showAll: boolean = false) {
        if (showAll) {
            this.skip = 0;
        }
        let orderedData = orderBy(
            this.filteredEmployees && !showAll ? this.filteredEmployees : this.employees,
            this.employeeSort
        );
        this.employeeGridView = {
            data: orderedData.slice(this.skip, this.skip + this.pageSize),
            total: orderedData.length,
        };
    }

    filterEmployees(): void {
        if (this.employeeSearchTerm === '') {
            this.loadEmployeesIntoGridView(true);
        } else {
            this.parentSearchTerm = '';
            let searchTermTokens = this.employeeSearchTerm.split(' ');
            this.filteredEmployees = filterBy(this.employees, {
                logic: 'or',
                filters: [
                    {
                        field: 'firstName',
                        operator: 'startswith',
                        value: searchTermTokens[0],
                        ignoreCase: true,
                    },
                    {
                        field: 'lastName',
                        operator: 'startswith',
                        value: searchTermTokens[0],
                        ignoreCase: true,
                    },
                    {
                        field: 'lfName',
                        operator: 'startswith',
                        value: searchTermTokens[0],
                        ignoreCase: true,
                    },
                ],
            });
            if (searchTermTokens.length > 1) {
                this.filteredEmployees = filterBy(this.filteredEmployees, {
                    logic: 'or',
                    filters: [
                        {
                            field: 'firstName',
                            operator: 'startswith',
                            value: searchTermTokens[1],
                            ignoreCase: true,
                        },
                        {
                            field: 'lastName',
                            operator: 'startswith',
                            value: searchTermTokens[1],
                            ignoreCase: true,
                        },
                        {
                            field: 'lfName',
                            operator: 'startswith',
                            value: searchTermTokens[1],
                            ignoreCase: true,
                        },
                    ],
                });
            }
            let orderedData = orderBy(this.filteredEmployees, this.employeeSort);
            this.employeeGridView = {
                data: orderedData.slice(this.skip, this.skip + this.pageSize),
                total: orderedData.length,
            };
        }
    }

    get hierarchyIdentifier() {
        return this.addOrEditEntityForm.get('createOrEditEntityRequest').get('hierarchyIdentifier');
    }

    get parentKey() {
        return this.addOrEditEntityForm.get('createOrEditEntityRequest').get('parentKey');
    }

    get parentName() {
        return this.addOrEditEntityForm.get('createOrEditEntityRequest').get('parentName');
    }

    get name() {
        return this.addOrEditEntityForm.get('createOrEditEntityRequest').get('name');
    }

    get nameAbbreviated() {
        return this.addOrEditEntityForm.get('createOrEditEntityRequest').get('nameAbbreviated');
    }

    get activeDate() {
        return this.addOrEditEntityForm.get('createOrEditEntityRequest').get('activeDate');
    }

    get ownerIdentifier() {
        return this.addOrEditEntityForm.get('createOrEditEntityRequest').get('ownerIdentifier');
    }

    cancelEditCreateView(): void {
        this.createView = false;
        this.editView = false;
        this.selectedEntity = undefined;
        this.addOrEditEntityForm.reset();
    }

    filterEntities(greaterThanCurrentLevel: boolean = false): void {
        if (greaterThanCurrentLevel) {
            this.employeeSearchTerm = '';
            this.filteredEntities = filterBy(this.entities, {
                logic: 'and',
                filters: [
                    {
                        field: 'levelOrder',
                        operator: 'gt',
                        value: this.getRoleOrder(),
                        ignoreCase: true,
                    },
                ],
            });
        } else {
            if (this.onUnknownTab) {
                this.filteredEntities = this.unknownEntities;
            } else {
                this.filteredEntities = filterBy(this.entities, {
                    logic: 'and',
                    filters: [
                        {
                            field: 'levelOrder',
                            operator: 'eq',
                            value: this.getRoleOrder(),
                            ignoreCase: true,
                        },
                    ],
                });
            }
        }
        if (this.filteredEntities.length === 0) {
            this.entityGridView = {
                data: this.filteredEntities,
                total: 0,
            };
            return;
        }
        if (this.entitySearchTerm === '' && !greaterThanCurrentLevel) {
            this.loadEntitiesIntoGridView();
        } else {
            this.filteredEntities = filterBy(this.filteredEntities, {
                logic: 'or',
                filters: [
                    {
                        field: 'hierarchyIdentifier',
                        operator: 'startswith',
                        value: greaterThanCurrentLevel
                            ? this.parentSearchTerm
                            : this.entitySearchTerm,
                        ignoreCase: true,
                    },
                    {
                        field: 'name',
                        operator: 'contains',
                        value: greaterThanCurrentLevel
                            ? this.parentSearchTerm
                            : this.entitySearchTerm,
                        ignoreCase: true,
                    },
                    {
                        field: 'nameAbbreviated',
                        operator: 'contains',
                        value: greaterThanCurrentLevel
                            ? this.parentSearchTerm
                            : this.entitySearchTerm,
                        ignoreCase: true,
                    },
                    {
                        field: 'hierarchyIdentifier',
                        operator: 'contains',
                        value: greaterThanCurrentLevel
                            ? this.parentSearchTerm
                            : this.entitySearchTerm,
                        ignoreCase: true,
                    },
                    {
                        field: 'owner',
                        operator: 'contains',
                        value: greaterThanCurrentLevel
                            ? this.parentSearchTerm
                            : this.entitySearchTerm,
                        ignoreCase: true,
                    },
                ],
            });
            let orderedData = orderBy(this.filteredEntities, this.entitySort);
            if (greaterThanCurrentLevel) {
                this.parentGridView = {
                    data: orderedData.slice(this.skip, this.skip + this.pageSize),
                    total: orderedData.length,
                };
            } else {
                this.entityGridView = {
                    data: orderedData.slice(this.skip, this.skip + this.pageSize),
                    total: orderedData.length,
                };
            }
        }
    }

    entityPageChange({ skip, take }: PageChangeEvent): void {
        this.skip = skip;
        this.pageSize = take;
        this.loadEntitiesIntoGridView();
    }

    parentPageChange({ skip, take }: PageChangeEvent): void {
        this.skip = skip;
        this.pageSize = take;
        this.loadParentEntitiesIntoGridView();
    }

    loadEntitiesIntoGridView() {
        let orderedData = orderBy(this.filteredEntities, this.entitySort);
        this.entityGridView = {
            data: orderedData.slice(this.skip, this.skip + this.pageSize),
            total: orderedData.length,
        };
    }

    loadParentEntitiesIntoGridView() {
        let orderedData = orderBy(this.filteredEntities, this.entitySort);
        this.parentGridView = {
            data: orderedData.slice(this.skip, this.skip + this.pageSize),
            total: orderedData.length,
        };
    }

    parentSelectionChangeHandler(event: any): void {
        let parent: HierarchyEntity = event.selectedRows[0].dataItem;
        this.addOrEditEntityForm
            .get('createOrEditEntityRequest')
            .get('parentName')
            .setValue(parent.name);
        this.addOrEditEntityForm
            .get('createOrEditEntityRequest')
            .get('parentKey')
            .setValue(parent.hierarchyKey);
        this.parentSearchTerm = '';
        this.addOrEditEntityForm.markAsDirty();
    }

    reloadLevels() {
        this._hierarchyService.getCorporateAllHierarchy().subscribe((levels) => {
            this.levels = levels.filter((level) => level.isActive);
            this.setTopTabBarItems();
        });
    }

    reloadEntities() {
        this._hierarchyService.getHierarchyEntities().subscribe((entities) => {
            this.entities = entities;
            this.setCorporateEntity();
            if (this.tabBarItems && this.tabBarItems.length) {
                this.filterEntities();
            }
        });
    }

    loadLevelsAndEntities() {
        forkJoin(
            this._hierarchyService.getCorporateAllHierarchy(),
            this._hierarchyService.getHierarchyEntities()
        ).subscribe(([levels, entities]) => {
            this.levels = levels.filter((level) => level.isActive);
            this.activeCustomRoleNames = this.levels.map((level) => level.roleName);
            this.entities = entities;
            this.setCorporateEntity();
            this.separateOutUnknownEntities();
            this.setTopTabBarItems();
            this.addUnknownTabIfNeeded();
        });
    }

    separateOutUnknownEntities() {
        this.unknownEntities = this.entities.filter((entity) => {
            return (
                entity.activeDate === null &&
                entity.inActiveDate === null &&
                entity.parentName === this.corporateEntity.name
            );
        });
    }

    removeOwnerFromFormControl(): void {
        this.addOrEditEntityForm.get('createOrEditEntityRequest').get('ownerIdentifier').reset();
        this.addOrEditEntityForm.get('createOrEditEntityRequest').get('owner').reset();
    }

    // csv functions
    csvUploadSuccessHandler(event) {
        this.toastService.success('Branch CSV File uploaded.');
        this.showCsvUploadDialog = false;
    }
    csvUploadErrorHandler(event) {
        this.toastService.error('Branch CSV File uploaded with errors.');
        this.showCsvUploadDialog = false;
    }
    csvUploadFailHandler(event) {
        this.toastService.error('Branch CSV File upload failed.');
        this.showCsvUploadDialog = false;
    }
    csvValidationErrorHandler(event) {
        this.showCsvUploadDialog = false;
        if (event.error === 'FILE_MAX_SIZE') {
            this.toastService.error(
                'Size of file exceeds limit of ' + this.fileMaxSizeInMB + 'MB.'
            );
        } else if (event.error === 'FILE_MAX_COUNT') {
            this.toastService.error('Number of files exceeds limit of ' + this.fileMaxCount + '.');
        } else if (event.error === 'UPLOAD_TYPE') {
            this.toastService.error('File is not of type: ' + this.uploadType + '.');
        } else if (event.error === 'EXTENSIONS') {
            this.toastService.error('File is not of type: ' + this.uploadType + '.');
        } else {
            this.toastService.error('Error with file. Please check file and try again.');
        }
    }
    switchToCsvUploadView() {
        this.showCsvUploadDialog = true;
    }
    @HostListener('window:keydown', ['$event'])
    keyEvent(event: KeyboardEvent) {
        if (event.keyCode === 27) {
            if (this.showCsvUploadDialog) {
                this.showCsvUploadDialog = false;
            }
        }
    }
}
